import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, Button, LinearProgress, Alert, Stack,
  IconButton, Select, MenuItem, FormControl,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Science as ScienceIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// On-Call Rotation (preview). Matches DispatchPreview.js visual language.
// Mon-Thu: single weeknight on-call tech. Fri-Sat-Sun: two zones (NYC Metro + East End).

const T = {
  fontStack: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  text: '#0F172A', textMuted: '#64748B', textFaint: '#94A3B8',
  bg: '#F8FAFC', surface: '#FFFFFF', border: '#E2E8F0', borderSoft: '#EEF2F7',
  accent: '#0D2C54',
  shadowSm: '0 1px 2px rgba(15,23,42,0.04)',
  shadowMd: '0 1px 3px rgba(15,23,42,0.06), 0 4px 12px rgba(15,23,42,0.04)',
  radius: 12, radiusSm: 8,
  transition: 'all 150ms ease',
};

const ZONE_METRO = 'NYC_METRO';
const ZONE_EAST  = 'EAST_END';
const ZONE_STYLE = {
  [ZONE_METRO]: { label: 'NYC Metro',    bg: '#DBEAFE', fg: '#1D4ED8', border: '#BFDBFE' },
  [ZONE_EAST]:  { label: 'East End LI',  bg: '#F3E8FF', fg: '#7E22CE', border: '#E9D5FF' },
};

function startOfWeek(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt; };
const ymd = (d) => d.toISOString().slice(0, 10);
const fmtDay = (d) => d.toLocaleDateString('en-US', { weekday: 'long' });
const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const AVATAR_PALETTE = [
  { bg: '#DBEAFE', fg: '#1D4ED8' }, { bg: '#DCFCE7', fg: '#15803D' },
  { bg: '#FEF3C7', fg: '#B45309' }, { bg: '#FCE7F3', fg: '#BE185D' },
  { bg: '#E0E7FF', fg: '#4338CA' }, { bg: '#CCFBF1', fg: '#0F766E' },
  { bg: '#FFE4E6', fg: '#BE123C' }, { bg: '#F3E8FF', fg: '#7E22CE' },
];
const avatarFor = (name) => {
  const s = String(name || '');
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};
const initials = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]).join('').toUpperCase();

const AvatarChip = ({ name, size = 28 }) => {
  if (!name) return null;
  const c = avatarFor(name);
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%', bgcolor: c.bg, color: c.fg,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 600, fontSize: size * 0.42, fontFamily: T.fontStack, flexShrink: 0,
    }}>
      {initials(name)}
    </Box>
  );
};

const TechSelect = ({ techs, value, onChange, disabled, placeholder = '— unassigned —' }) => (
  <FormControl size="small" fullWidth disabled={disabled}>
    <Select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      displayEmpty
      sx={{
        fontFamily: T.fontStack, borderRadius: `${T.radiusSm}px`, bgcolor: T.surface,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.textMuted },
      }}
    >
      <MenuItem value=""><em style={{ color: T.textFaint }}>{placeholder}</em></MenuItem>
      {techs.map((t) => (
        <MenuItem key={t.id} value={t.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AvatarChip name={t.name} size={22} />
            <span>{t.name}</span>
          </Box>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const DispatchOnCall = () => {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [me, setMe]       = useState(null);
  const [techs, setTechs] = useState([]);
  const [oncall, setOncall] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const today = useMemo(() => new Date(), []);

  const token = () => localStorage.getItem('token');
  const canEdit = me?.authority && me.authority !== 'read';

  useEffect(() => {
    const h = { Authorization: `Bearer ${token()}` };
    Promise.all([
      axios.get('/api/dispatch-preview/me', { headers: h }),
      axios.get('/api/dispatch-preview/techs', { headers: h }),
    ]).then(([m, t]) => {
      setMe(m.data);
      setTechs(t.data?.techs || []);
    }).catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    axios.get('/api/dispatch-preview/oncall', {
      headers: { Authorization: `Bearer ${token()}` },
      params: { from: ymd(weekStart), to: ymd(weekEnd) },
    })
      .then((r) => setOncall(r.data?.oncall || []))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [weekStart, weekEnd]);
  useEffect(refresh, [refresh]);

  const byKey = useMemo(() => {
    const m = {};
    oncall.forEach((o) => { m[`${o.on_date.slice(0, 10)}|${o.zone || ''}`] = o; });
    return m;
  }, [oncall]);

  const setOn = async (date, zone, techId) => {
    const h = { Authorization: `Bearer ${token()}` };
    try {
      if (!techId) {
        const existing = byKey[`${date}|${zone || ''}`];
        if (existing) await axios.delete(`/api/dispatch-preview/oncall/${existing.id}`, { headers: h });
      } else {
        await axios.post('/api/dispatch-preview/oncall', { on_date: date, tech_id: techId, zone }, { headers: h });
      }
      refresh();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  // Card for a single assignment slot
  const SlotCard = ({ date, zone, label, chipStyle }) => {
    const row = byKey[`${date}|${zone || ''}`];
    const techName = techs.find((t) => t.id === row?.tech_id)?.name;
    return (
      <Box sx={{
        p: 2, borderRadius: `${T.radiusSm}px`,
        border: `1px solid ${T.border}`, bgcolor: T.surface,
        display: 'flex', alignItems: 'center', gap: 2,
        transition: T.transition,
        '&:hover': { boxShadow: T.shadowSm },
      }}>
        {chipStyle && (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', px: 1, py: 0.4,
            borderRadius: 1, bgcolor: chipStyle.bg, color: chipStyle.fg,
            fontSize: 11, fontWeight: 700, border: `1px solid ${chipStyle.border}`,
            whiteSpace: 'nowrap', minWidth: 90, justifyContent: 'center',
          }}>
            {chipStyle.label}
          </Box>
        )}
        {!chipStyle && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.textMuted, minWidth: 90 }}>
            {label}
          </Typography>
        )}
        <Box sx={{ flex: 1 }}>
          <TechSelect techs={techs} value={row?.tech_id} disabled={!canEdit}
            onChange={(v) => setOn(date, zone, v || null)} />
        </Box>
        {techName && (
          <AvatarChip name={techName} size={28} />
        )}
        {row && canEdit && (
          <IconButton size="small" onClick={() => setOn(date, zone, null)}
            sx={{ color: T.textFaint, '&:hover': { color: T.text, bgcolor: T.borderSoft } }}>
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: T.fontStack, bgcolor: T.bg, minHeight: '100vh', color: T.text }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.5px', color: T.text, lineHeight: 1.1 }}>
            On-Call Rotation
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.textMuted, mt: 0.5 }}>
            Separate from the dispatch Gantt · Drives Friday 6 PM weekend coverage email
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Button startIcon={<ArrowBackIcon fontSize="small" />} onClick={() => navigate('/dispatch-template')}
            sx={{ textTransform: 'none', fontWeight: 500, color: T.textMuted, borderRadius: `${T.radiusSm}px`,
              '&:hover': { bgcolor: T.borderSoft, color: T.text } }}>
            Back to Dispatch
          </Button>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: `${T.radiusSm}px` }}>{error}</Alert>}

      {/* Week toolbar */}
      <Paper elevation={0} sx={{
        p: 2, mb: 2.5, borderRadius: `${T.radius}px`,
        bgcolor: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadowSm,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <IconButton onClick={() => setWeekStart(addDays(weekStart, -7))} size="small"
          sx={{ border: `1px solid ${T.border}`, borderRadius: `${T.radiusSm}px`, transition: T.transition,
            '&:hover': { bgcolor: T.borderSoft } }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => setWeekStart(addDays(weekStart, 7))} size="small"
          sx={{ border: `1px solid ${T.border}`, borderRadius: `${T.radiusSm}px`, transition: T.transition,
            '&:hover': { bgcolor: T.borderSoft } }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <Button onClick={() => setWeekStart(startOfWeek(new Date()))}
          sx={{ textTransform: 'none', fontWeight: 500, color: T.textMuted, borderRadius: `${T.radiusSm}px` }}>
          Today
        </Button>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text, ml: 1 }}>
          {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
          {' – '}
          {weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </Typography>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1, height: 2 }} />}

      {/* Day cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {days.map((d) => {
          const dateKey = ymd(d);
          const dow = d.getDay();
          const weekend = dow === 5 || dow === 6 || dow === 0;
          const todayDay = isSameDay(d, today);

          return (
            <Paper key={dateKey} elevation={0} sx={{
              borderRadius: `${T.radius}px`, bgcolor: T.surface,
              border: `1px solid ${T.border}`, boxShadow: T.shadowSm,
              overflow: 'hidden',
            }}>
              {/* Day header */}
              <Box sx={{
                px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
                borderBottom: weekend ? `1px solid ${T.borderSoft}` : 'none',
              }}>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 44, height: 44, borderRadius: `${T.radiusSm}px`,
                  bgcolor: todayDay ? T.accent : T.borderSoft,
                  color: todayDay ? '#fff' : T.text,
                  fontWeight: 700, fontSize: 18,
                }}>
                  {d.getDate()}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>
                    {fmtDay(d)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: T.textMuted }}>
                    {fmtDate(d)}{todayDay ? ' · today' : ''}
                  </Typography>
                </Box>
                <Chip size="small" label={weekend ? 'Weekend coverage' : 'Weeknight'}
                  sx={{
                    bgcolor: weekend ? '#FEF3C7' : T.borderSoft,
                    color: weekend ? '#B45309' : T.textMuted,
                    fontWeight: 600, borderRadius: 1.5,
                    border: weekend ? '1px solid #FDE68A' : 'none',
                  }} />
              </Box>

              {/* Slots */}
              <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {!weekend ? (
                  <SlotCard date={dateKey} zone={null} label="On-Call" />
                ) : (
                  <>
                    <SlotCard date={dateKey} zone={ZONE_METRO} chipStyle={ZONE_STYLE[ZONE_METRO]} />
                    <SlotCard date={dateKey} zone={ZONE_EAST}  chipStyle={ZONE_STYLE[ZONE_EAST]}  />
                  </>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>

      <Typography sx={{ display: 'block', mt: 3, fontSize: 12, color: T.textFaint, px: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScienceIcon sx={{ fontSize: 14 }} />
        On-call techs aren't on the Gantt — they're the if-something-breaks rotation. Mon-Thu: one tech per night. Fri 6 PM – Mon AM: two techs (NYC Metro + East End). Friday's 6 PM email surfaces both weekend assignments.
      </Typography>
    </Box>
  );
};

export default DispatchOnCall;
